import { ref, computed } from 'vue'

// Product data
const products = {
  yumi: {
    id: 'yumi',
    name: 'ユミラム',
    price: 6000,
    currency: 'jpy',
    image: '/image/SC-YRSV-750-YRB2_quarter.png',
    description: 'Yumi Rum - バヌアツの心が伝わる'
  },
  aged: {
    id: 'aged',
    name: 'エイジドラム',
    price: 7000,
    currency: 'jpy',
    image: '/image/ARB4-Normal_quarter.png',
    description: 'Aged Rum - 熟成された味わい'
  },
  lostTime: {
    id: 'lostTime',
    name: 'エイジドラム「ロストタイム」',
    price: 8000,
    currency: 'jpy',
    image: '/image/ARB10_quarter.png',
    description: 'Lost Time Aged Rum - 特別な熟成'
  }
}

// Shipping zones and rates
const shippingZones = {
  mainland: {
    name: '本州・四国・九州',
    rate: 1300,
    freeThreshold: 25000,
    prefectures: [
      'Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka', 'Fukushima',
      'Gifu', 'Gunma', 'Hiroshima', 'Hyogo', 'Ibaraki', 'Ishikawa', 'Iwate', 'Kagawa',
      'Kagoshima', 'Kanagawa', 'Kochi', 'Kumamoto', 'Kyoto', 'Mie', 'Miyagi', 'Miyazaki',
      'Nagano', 'Nagasaki', 'Nara', 'Niigata', 'Oita', 'Okayama', 'Osaka', 'Saga',
      'Saitama', 'Shiga', 'Shimane', 'Shizuoka', 'Tochigi', 'Tokushima', 'Tokyo',
      'Tottori', 'Toyama', 'Wakayama', 'Yamagata', 'Yamaguchi', 'Yamanashi'
    ]
  },
  remote: {
    name: '北海道・沖縄・離島',
    rate: 2600,
    freeThreshold: 35000,
    prefectures: ['Hokkaido', 'Okinawa']
  }
}

export function useCart() {
  const cartItems = ref([])
  const isCartOpen = ref(false)
  const shippingAddress = ref(null)
  const shippingZone = ref(null)

  // Add item to cart
  const addToCart = (productId) => {
    const product = products[productId]
    if (!product) return

    const existingItem = cartItems.value.find(item => item.id === productId)
    
    if (existingItem) {
      existingItem.quantity += 1
    } else {
      cartItems.value.push({
        id: productId,
        name: product.name,
        price: product.price,
        currency: product.currency,
        image: product.image,
        description: product.description,
        quantity: 1
      })
    }
  }

  // Remove item from cart
  const removeFromCart = (productId) => {
    const index = cartItems.value.findIndex(item => item.id === productId)
    if (index > -1) {
      cartItems.value.splice(index, 1)
    }
  }

  // Update item quantity
  const updateQuantity = (productId, quantity) => {
    const item = cartItems.value.find(item => item.id === productId)
    if (item) {
      if (quantity <= 0) {
        removeFromCart(productId)
      } else {
        item.quantity = quantity
      }
    }
  }

  // Clear cart
  const clearCart = () => {
    cartItems.value = []
  }

  // Calculate shipping zone based on address
  const calculateShippingZone = (address) => {
    if (!address || !address.state) return null
    
    const state = address.state
    const isRemote = shippingZones.remote.prefectures.includes(state)
    
    return isRemote ? 'remote' : 'mainland'
  }

  // Calculate shipping cost
  const calculateShippingCost = (subtotal, zone) => {
    if (!zone) return 0
    
    const zoneConfig = shippingZones[zone]
    if (!zoneConfig) return 0
    
    // Check if free shipping threshold is met
    if (subtotal >= zoneConfig.freeThreshold) {
      return 0
    }
    
    // Calculate shipping per bottle
    const totalBottles = cartItems.value.reduce((count, item) => count + item.quantity, 0)
    return zoneConfig.rate * totalBottles
  }

  // Computed properties
  const cartSubtotal = computed(() => {
    return cartItems.value.reduce((total, item) => {
      return total + (item.price * item.quantity)
    }, 0)
  })

  const shippingCost = computed(() => {
    return calculateShippingCost(cartSubtotal.value, shippingZone.value)
  })

  const cartTotal = computed(() => {
    return cartSubtotal.value + shippingCost.value
  })

  const cartCount = computed(() => {
    return cartItems.value.reduce((count, item) => {
      return count + item.quantity
    }, 0)
  })

  const isEmpty = computed(() => {
    return cartItems.value.length === 0
  })

  const shippingZoneName = computed(() => {
    if (!shippingZone.value) return null
    return shippingZones[shippingZone.value]?.name
  })

  const isFreeShipping = computed(() => {
    if (!shippingZone.value) return false
    const zoneConfig = shippingZones[shippingZone.value]
    return cartSubtotal.value >= zoneConfig.freeThreshold
  })

  // Toggle cart visibility
  const toggleCart = () => {
    isCartOpen.value = !isCartOpen.value
  }

  const openCart = () => {
    isCartOpen.value = true
  }

  const closeCart = () => {
    isCartOpen.value = false
  }

  return {
    products,
    shippingZones,
    cartItems,
    isCartOpen,
    shippingAddress,
    shippingZone,
    cartSubtotal,
    shippingCost,
    cartTotal,
    cartCount,
    isEmpty,
    shippingZoneName,
    isFreeShipping,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    calculateShippingZone,
    toggleCart,
    openCart,
    closeCart
  }
}
